import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";

import { getUrlParam } from "utils";
import { selectAPIKey, selectPropertyId, setBudgets, unitSliceData } from "slices";

import { SelectComponent } from "../components";
import MoveInPicker from "../components/MoveInPicker";
import { budgetFormat, getPriceRange, getPriceRangeApi } from "../utils";

const MOVE_IN_OPTIONS = [
  {
    label: "ASAP",
    value: "asap", // moment().add(7, "days").format("YYYY-MM-DD"),
    trans: "asap",
    date: moment().add(7, "days").format("YYYY-MM-DD"),
  },
  {
    label: "Next 30 days",
    value: "next_30_days", // moment().add(30, "days").format("YYYY-MM-DD"),
    trans: "next30Days",
    date: moment().add(30, "days").format("YYYY-MM-DD"),
  },
  {
    label: "Next 60 days",
    value: "next_60_days", // moment().add(60, "days").format("YYYY-MM-DD"),
    trans: "next60Days",
    date: moment().add(60, "days").format("YYYY-MM-DD"),
  },
  {
    label: "Custom Range",
    value: "custom",
    trans: "customRange",
  },
  {
    label: "Not Sure",
    value: "any",
    trans: "notSure",
    date: "",
  },
];

// Check if date is in "Nov 25, 2024" format
const isDateInFormat = (dateString) => {
  const format = "MMM DD, YYYY";

  return moment(dateString, format, true).isValid();
};

const TopBar = ({
  filterRef,
  filterData,
  unitsCount,
  allTableData,
  setFilterData,
  getFilteredUnits,
  currentFloorPlans,
  filterByFloorPlan,
  desiredMoveInDate,
  setDesiredMoveInDate,
}) => {
  const unitData = useSelector(unitSliceData);
  const propertyId = useSelector(selectPropertyId);
  const apiKey = useSelector(selectAPIKey);

  const dispatch = useDispatch();

  const { filters } = unitData;
  const [selectorType, setSelectorType] = useState(false);
  const [isAny, setIsAny] = useState(false);
  const [customBudget, setCustomBudget] = useState(false);
  const [priceRanges, setPriceRanges] = useState({});

  useEffect(() => {
    if (filters) {
      setDesiredMoveInDate(isDateInFormat(filters?.moveIns) ? moment(filters?.moveIns).format("YYYY-MM-DD") : filters?.moveIns || "");
      setSelectorType(false);

      if (filters?.budgets) {
        let range = getPriceRange(filterData.budgets);
        setCustomBudget(range);
      }
    } else {
      setSelectorType("All");
    }
  }, [filters]);

  const saveRange = async (val) => {
    const res = await getPriceRangeApi(propertyId, apiKey, val);

    dispatch(setBudgets(res?.data || null));

    return res?.data;
  };

  const handleSelectedValue = async (val, key, isAsap = false) => {
    let filter;

    if (filterData?.[key] == val) {
      return;
    }

    if (key == "bedrooms") {
      if (val === "All") {
        filter = null;
        setSelectorType(val);
        setFilterData(filter);
        getFilteredUnits(filter);

        return;
      }

      setSelectorType(false);

      let res;

      if (priceRanges[val]) {
        res = priceRanges[val];
        dispatch(setBudgets(res || null));
      } else {
        res = await saveRange(val);

        setPriceRanges((prev) => ({
          ...prev,
          [val]: res,
        }));
      }

      const anyRange = budgetFormat(res?._custom_price_range);

      const budgetRange = budgetFormat(unitData?.budgets?._custom_price_range);

      const isAnySelected = budgetRange === filterData?.budgets;

      filter = {
        ...filterData,
        isAsap,
        [key]: val,
        budgets: anyRange,
        allFloorPlans: { id: null, name: "All" },
        moveIns: "any",
      };

      setFilterData(filter);

      if (isAnySelected) {
        setIsAny(true);
      }
    } else {
      if (key == "budgets") {
        const obj = getPriceRange(val);
        setCustomBudget(obj);
      }

      filter = {
        ...filterData,
        isAsap,
        [key]: val,
      };

      if (key === "allFloorPlans" && val == null) {
        filter.allFloorPlans = { id: null, name: "All" };
      }

      setFilterData(filter);
    }

    if (key != "allFloorPlans") {
      getFilteredUnits(filter);
    } else {
      filterByFloorPlan(allTableData, filter);
    }
  };

  const dBudget = getUrlParam("budget");

  return (
    <div
      ref={filterRef}
      className="hayley-flex hayley-w-full hayley-items-center hayley-justify-between hayley-border hayley-border-solid hayley-border-custom-2 hayley-p-2"
    >
      <div className="hayley-flex hayley-flex-col hayley-flex-wrap hayley-justify-start sm:hayley-flex-row sm:hayley-items-center ">
        <SelectComponent
          isAny={isAny}
          item={"bedrooms"}
          filterData={filterData}
          selectorType={selectorType}
          defaultValue={!!filterData && filterData?.bedrooms}
          handleSelectedValue={handleSelectedValue}
        />
        <div className="hayley-mx-2 hayley-hidden hayley-h-[15px] hayley-w-[1px] hayley-bg-secondary hayley-bg-opacity-20 sm:hayley-block" />

        <SelectComponent
          isAny={isAny}
          item={"allFloorPlans"}
          filterData={filterData}
          data={currentFloorPlans}
          selectorType={selectorType}
          defaultValue={!!filterData && filterData?.allFloorPlans}
          handleSelectedValue={handleSelectedValue}
          key={filters?.allFloorPlans?.id === null ? "allFloorPlans-1" : "allFloorPlans"}
        />
        <div className="hayley-mx-2 hayley-hidden hayley-h-[15px] hayley-w-[1px] hayley-bg-secondary  hayley-bg-opacity-20 sm:hayley-block" />

        <SelectComponent
          isAny={isAny}
          item={"budgets"}
          filterData={filterData}
          customBudget={customBudget}
          selectorType={selectorType}
          defaultValue={!dBudget && !!filterData && filterData?.budgets}
          handleSelectedValue={handleSelectedValue}
          key={filterData === null ? "budgets-1" : "budgets"}
        />
        <div className="hayley-mx-2 hayley-hidden hayley-h-[15px] hayley-w-[1px] hayley-bg-secondary  hayley-bg-opacity-20 sm:hayley-block" />

        <MoveInPicker options={MOVE_IN_OPTIONS} onSelect={handleSelectedValue} value={desiredMoveInDate} selectorType={selectorType} filterData={filterData} />
      </div>
      <div className="total-units">
        {unitsCount} {t("units")}
      </div>
    </div>
  );
};

export default TopBar;
